import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { useShallowEqualSelector } from '@/hooks';
import { useHasAccess } from '@/hooks/user';
import { VIEWS_NAMES } from '@/pages/Collaborators/CollaboratosModal/constants';
import { capitalize, cn } from '@/utils';

import Billing from './Billing';
import * as Forms from './Forms/Forms';
import { GENERAL_INFO_TAB, tabsAndSections } from './constants';

const sectionComponents = { ...Forms, Billing };

dayjs.extend(localizedFormat);

function Account() {
	const { t } = useTranslation();
	const stateCurrentTab = useLocation().state?.currentTab;

	const { currentTab, updatedAt } = useShallowEqualSelector(({ profile }) => ({
		currentTab: profile.currentTab,
		updatedAt: profile.user.updatedAt
	}));
	const hasAccess = useHasAccess();
	const canUserSeeTaxInfo = hasAccess(VIEWS_NAMES.TAX_INFO);
	const filteredTabs = tabsAndSections.filter(
		({ name }) => canUserSeeTaxInfo || name !== VIEWS_NAMES.TAX_INFO
	);

	return (
		<div className="px-5 md:px-8">
			<h1 className="mt-[56px] font-title text-[32px] font-semibold leading-10">
				{t('pageTitles.myAccount')}
			</h1>
			<div className="mt-10 flex w-full">
				<Tabs
					className="w-full"
					defaultValue={stateCurrentTab || currentTab || GENERAL_INFO_TAB}
				>
					<TabsList className="mb-3 flex justify-start gap-4 lg:mb-4">
						{filteredTabs.map(({ name }) => {
							return (
								<TabsTrigger
									className="rounded data-[state=active]:bg-red-background"
									value={name}
									key={name}
								>
									{t(`account.tabs.${name}`)}
								</TabsTrigger>
							);
						})}
					</TabsList>

					{filteredTabs.map(
						({ name, sections, showUpdatedBanner = true }) => {
							return (
								<TabsContent
									value={name}
									className={cn(
										'mx-0 mb-6 flex max-w-full flex-col gap-4 lg:mb-[100px] lg:gap-6',
										!showUpdatedBanner && 'border-t border-grey-200'
									)}
									key={name}
								>
									{showUpdatedBanner && (
										<div className="mb-2 rounded bg-grey-100 px-6 py-1 text-center text-2xs text-grey-500 md:text-left md:text-xs md:leading-5">
											{t('common.updatedOn', {
												date: dayjs.unix(updatedAt).format('LL')
											})}
										</div>
									)}

									{sections.map(section => {
										const CurrentSection =
											sectionComponents[capitalize(section)];

										const sectionI18nKey = `account.sections.${section}`;

										return (
											<CurrentSection
												key={section}
												title={t(`${sectionI18nKey}.title`)}
												description={t(
													`${sectionI18nKey}.description`
												)}
											/>
										);
									})}
								</TabsContent>
							);
						}
					)}
				</Tabs>
			</div>
		</div>
	);
}

export default Account;
